import { FullSingleQuote } from "@/types";
import { arrayToCSV, downloadCSV } from "@/utils/Exports/export.util";
import { formatDatePretty, genericFailureToast } from "@/utils/utils.util";
import * as XLSX from "xlsx";

// #region exportSingleQuoteToExcel
export function exportSingleQuoteToExcel(singleQuote: FullSingleQuote) {
  try {
    // Using xlsx for broad compatibility with Excel and ease of use
    // Prepare quote details as a single-row object for clarity in Excel
    const quoteDetails = [
      {
        "Quote ID": singleQuote.id,
        "Branch ID": singleQuote.branch_id,
        "User ID": singleQuote.created_by_user_id,
        "Quoted By": singleQuote.quoted_by,
        "File ID": singleQuote.file_id,
        "Quoted By Name": singleQuote.quoted_by_name,
        "File Drawing No": singleQuote.file_drawing_no,
        "Quote Number": singleQuote.quote_number,
        "VDS Job Number": singleQuote.vds_job_number,
        "Customer Reference": singleQuote.customer_reference,
        Status: singleQuote.quote_status,
        "Line No": singleQuote.line_no,
        "VAT Amount": singleQuote.vat_amount,
        "Total Excl VAT": singleQuote.total_excl_vat,
        "Total Incl VAT": singleQuote.total_incl_vat,
        "Created At": formatDatePretty(
          singleQuote.quote_created_at,
          true,
          false
        ),
        "Updated At": formatDatePretty(
          singleQuote.quote_updated_at,
          true,
          false
        ),
        "Expiry At": formatDatePretty(singleQuote.quote_expiry_at, true, false),
        "Approval At": formatDatePretty(
          singleQuote.quote_approval_at,
          true,
          false
        ),
      },
    ];

    // Flatten quote items for tabular representation in Excel
    const quoteItems = singleQuote.quote_items.map((item) => ({
      "Line Item Number": singleQuote.line_no,
      "Item ID": item.id,
      "Quote ID": item.quote_id,
      "Drawing No": item.item_file_drawing_no,
      "Part Name": item.name,
      Description: item.description || "",
      "Material Type": item.material_type,
      "Material Thickness": item.thickness,
      "Material Width": item.width,
      "Material Length": item.length,
      "Quantity Ordered": item.qty,
      "Price Per Item": item.price_per_item || "",
      "Total Price Per Item": item.total_price_per_item || "",
      "Process Type": item.process_type || "",
      "Bending Tolerance": item.bending_tolerance || "",
      "Created At": formatDatePretty(item.item_created_at, true, false),
    }));

    // Creating workbook and sheets for separation of concerns and user clarity
    const wb = XLSX.utils.book_new();
    const wsQuoteDetails = XLSX.utils.json_to_sheet(quoteDetails);
    const wsQuoteItems = XLSX.utils.json_to_sheet(quoteItems);
    // Set column widths to improve readability for longer names
    wsQuoteDetails["!cols"] = [
      { wch: 15 }, // Quote ID
      { wch: 12 }, // Branch ID
      { wch: 12 }, // User ID
      { wch: 18 }, // Quoted By
      { wch: 12 }, // File ID
      { wch: 20 }, // Quoted By Name
      { wch: 18 }, // File Drawing No
      { wch: 15 }, // Quote Number
      { wch: 15 }, // VDS Job Number
      { wch: 20 }, // Customer Reference
      { wch: 10 }, // Status
      { wch: 8 }, // Line No
      { wch: 12 }, // VAT Amount
      { wch: 16 }, // Total Excl VAT
      { wch: 16 }, // Total Incl VAT
      { wch: 24 }, // Created At
      { wch: 24 }, // Updated At
      { wch: 24 }, // Expiry At
      { wch: 24 }, // Approval At
    ];

    wsQuoteItems["!cols"] = [
      { wch: 12 }, // Item ID
      { wch: 12 }, // Quote ID
      { wch: 10 }, // Length
      { wch: 10 }, // Width
      { wch: 16 }, // Material Type
      { wch: 14 }, // Thickness
      { wch: 8 }, // Quantity
      { wch: 22 }, // Name
      { wch: 30 }, // Description
      { wch: 16 }, // Process Type
      { wch: 20 }, // Bending Tolerance
      { wch: 16 }, // Price Per Item
      { wch: 16 }, // Total Price Per Item
      { wch: 24 }, // Created At
    ];

    XLSX.utils.book_append_sheet(wb, wsQuoteDetails, "Quote Details");
    XLSX.utils.book_append_sheet(wb, wsQuoteItems, "Quote Items");

    // Use quote id in filename for traceability
    XLSX.writeFile(wb, `Quote_${singleQuote.quote_number}.xlsx`);
  } catch (error) {
    console.error("Error exporting quote to Excel:", error);
    genericFailureToast("Failed to export quote to Excel. Please try again.");
  }
}
// #endregion exportSingleQuoteToExcel

// #region exportSingleQuoteDetailsToCSV
function exportSingleQuoteDetailsToCSV(
  singleQuoteDetails: Omit<FullSingleQuote, "quote_items">
) {
  // Exporting as single-row CSV for clarity and to match Excel export structure
  try {
    const details = [
      {
        "Quote ID": singleQuoteDetails.id,
        "Branch ID": singleQuoteDetails.branch_id,
        "User ID": singleQuoteDetails.created_by_user_id,
        "Quoted By": singleQuoteDetails.quoted_by,
        "File ID": singleQuoteDetails.file_id,
        "Quoted By Name": singleQuoteDetails.quoted_by_name,
        "File Drawing No": singleQuoteDetails.file_drawing_no,
        "Quote Number": singleQuoteDetails.quote_number,
        "VDS Job Number": singleQuoteDetails.vds_job_number,
        "Customer Reference": singleQuoteDetails.customer_reference,
        Status: singleQuoteDetails.quote_status,
        "Line No": singleQuoteDetails.line_no,
        "VAT Amount": singleQuoteDetails.vat_amount,
        "Total Excl VAT": singleQuoteDetails.total_excl_vat,
        "Total Incl VAT": singleQuoteDetails.total_incl_vat,
        "Created At": formatDatePretty(
          singleQuoteDetails.quote_created_at,
          true,
          false
        ),
        "Updated At": formatDatePretty(
          singleQuoteDetails.quote_updated_at,
          true,
          false
        ),
        "Expiry At": formatDatePretty(
          singleQuoteDetails.quote_expiry_at,
          true,
          false
        ),
        "Approval At": formatDatePretty(
          singleQuoteDetails.quote_approval_at,
          true,
          false
        ),
      },
    ];
    const csv = arrayToCSV(details);
    // Using quote number for filename to aid traceability
    downloadCSV(csv, `Quote_${singleQuoteDetails.quote_number}_Details.csv`);
  } catch (error) {
    console.error("Error exporting quote details to CSV:", error);
    genericFailureToast(
      "Failed to export quote details to CSV. Please try again."
    );
  }
}

// #endregion exportSingleQuoteDetailsToCSV
// #region exportSingleQuoteItemsToCSV
function exportSingleQuoteItemsToCSV(singleQuote: FullSingleQuote) {
  // Exporting items as tabular CSV for compatibility with spreadsheet tools

  const singleQuoteItems = singleQuote.quote_items;
  try {
    const items = singleQuoteItems.map((item) => ({
      "Line Item Number": singleQuote.line_no,
      "Item ID": item.id,
      "Quote ID": item.quote_id,
      "Drawing No": item.item_file_drawing_no,
      "Part Name": item.name,
      Description: item.description || "",
      "Material Type": item.material_type,
      "Material Thickness": item.thickness,
      "Material Width": item.width,
      "Material Length": item.length,
      "Quantity Ordered": item.qty,
      "Price Per Item": item.price_per_item || "",
      "Total Price Per Item": item.total_price_per_item || "",
      "Process Type": item.process_type || "",
      "Bending Tolerance": item.bending_tolerance || "",
      "Created At": formatDatePretty(item.item_created_at, true, false),
    }));
    const csv = arrayToCSV(items);
    downloadCSV(csv, `Quote_${singleQuote.id}_Items.csv`);
  } catch (error) {
    console.error("Error exporting quote items to CSV:", error);
    genericFailureToast(
      "Failed to export quote items to CSV. Please try again."
    );
  }
}
// #endregion exportSingleQuoteItemsToCSV

// #region exportSingleQuoteToCSVs
export function exportSingleQuoteToCSVs(singleQuote: FullSingleQuote) {
  // Keeping this function as orchestrator for both CSV exports for clarity
  try {
    exportSingleQuoteDetailsToCSV(singleQuote);
    exportSingleQuoteItemsToCSV(singleQuote);
  } catch (error) {
    console.error("Error exporting quote to CSVs:", error);
    genericFailureToast("Failed to export quote to CSVs. Please try again.");
  }
}

// #endregion exportSingleQuoteToCSVs
